import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import type { ConsultationRow, LevelKey } from '@/pages/admin/types';
import { levelOf } from '@/pages/admin/utils/level';
import { exportConsultations } from '@/pages/admin/utils/csv';
import AdminHeader from '@/pages/admin/components/AdminHeader';
import FilterBar from '@/pages/admin/components/FilterBar';
import ConsultationCard from '@/pages/admin/components/ConsultationCard';
import ConsultationDetail from '@/pages/admin/components/ConsultationDetail';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [email, setEmail] = useState<string | null>(null);
  const [rows, setRows] = useState<ConsultationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [forbidden, setForbidden] = useState(false);
  const [filter, setFilter] = useState<LevelKey | 'all'>('all');
  const [active, setActive] = useState<ConsultationRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setForbidden(false);

    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;

      const session = sessionData.session;
      if (!session) {
        navigate('/admin/login', { replace: true });
        return;
      }

      const userEmail = session.user.email ?? '';
      setEmail(userEmail);

      const { data: adminRow, error: adminError } = await supabase
        .from('admins')
        .select('email')
        .eq('email', userEmail)
        .maybeSingle();
      if (adminError) throw adminError;

      if (!adminRow) {
        setForbidden(true);
        return;
      }

      const { data, error: listError } = await supabase
        .from('consultations')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(500);
      if (listError) throw listError;

      setRows((data ?? []) as ConsultationRow[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : '数据加载失败，稍后再试');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        navigate('/admin/login', { replace: true });
      }
    });
    return () => {
      data.subscription.unsubscribe();
    };
  }, [navigate]);

  const counts = useMemo(() => {
    const result: Record<string, number> = { mild: 0, moderate: 0, severe: 0, none: 0 };
    rows.forEach((row) => {
      result[levelOf(row).key] += 1;
    });
    return result;
  }, [rows]);

  const filtered = useMemo(() => {
    if (filter === 'all') return rows;
    return rows.filter((row) => levelOf(row).key === filter);
  }, [rows, filter]);

  const handleLogout = useCallback(async () => {
    await supabase.auth.signOut();
    navigate('/admin/login', { replace: true });
  }, [navigate]);

  const handleExport = useCallback(() => {
    exportConsultations(filtered);
  }, [filtered]);

  return (
    <div className="min-h-screen bg-background-100 flex flex-col">
      <AdminHeader
        email={email}
        onLogout={handleLogout}
        onRefresh={() => void load()}
        refreshing={loading}
      />

      <main className="flex-1 w-full max-w-5xl mx-auto px-4 md:px-6 py-4 md:py-6">
        <FilterBar
          active={filter}
          counts={counts}
          total={rows.length}
          onChange={setFilter}
          onExport={handleExport}
          exportDisabled={loading || filtered.length === 0}
          visibleCount={filtered.length}
        />

        <div className="mt-4">
          {loading ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {[0, 1, 2, 3].map((item) => (
                <div
                  key={item}
                  className="h-[136px] rounded-xl bg-background-50 border border-secondary-200/70 animate-pulse"
                />
              ))}
            </div>
          ) : null}

          {!loading && forbidden ? (
            <div className="bg-background-50 rounded-xl border border-accent-300 p-6 text-center">
              <i className="ri-shield-keyhole-line text-2xl text-accent-600"></i>
              <p className="text-sm font-semibold text-foreground-900 mt-2">这个账号没有后台权限</p>
              <p className="text-xs text-foreground-500 mt-1.5 leading-relaxed">
                当前登录的是 {email || '未知账号'}，需要把它加进管理员名单才能查看问诊记录。
              </p>
              <button
                type="button"
                onClick={handleLogout}
                className="mt-4 h-9 px-4 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                换个账号登录
              </button>
            </div>
          ) : null}

          {!loading && !forbidden && error ? (
            <div className="bg-background-50 rounded-xl border border-accent-300 p-6 text-center">
              <i className="ri-error-warning-line text-2xl text-accent-600"></i>
              <p className="text-sm font-semibold text-foreground-900 mt-2">数据没加载出来</p>
              <p className="text-xs text-foreground-500 mt-1.5 break-words leading-relaxed">{error}</p>
              <button
                type="button"
                onClick={() => void load()}
                className="mt-4 h-9 px-4 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                重新加载
              </button>
            </div>
          ) : null}

          {!loading && !forbidden && !error && filtered.length === 0 ? (
            <div className="bg-background-50 rounded-xl border border-secondary-200/70 p-10 text-center">
              <i className="ri-inbox-archive-line text-3xl text-secondary-400"></i>
              <p className="text-sm font-medium text-foreground-800 mt-2">
                {rows.length === 0 ? '还没有人提交问诊表' : '这个筛选下还没有记录'}
              </p>
              <p className="text-xs text-foreground-400 mt-1.5">
                {rows.length === 0 ? '有人提交后，这里会自动出现记录' : '换个筛选条件看看'}
              </p>
            </div>
          ) : null}

          {!loading && !forbidden && !error && filtered.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {filtered.map((row) => (
                <ConsultationCard key={row.id} row={row} onOpen={setActive} />
              ))}
            </div>
          ) : null}
        </div>

        <p className="text-[11px] text-foreground-400 text-center mt-6 leading-relaxed">
          数据来自 Supabase · 只有管理员名单里的账号能看到这些记录
        </p>
      </main>

      <ConsultationDetail row={active} onClose={() => setActive(null)} />
    </div>
  );
}